import React, {FC, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector, useStore} from 'react-redux';
import {RouteName} from '@/app/constants/routing';
import {LocationType} from '@/common/constants/locationType';
import {MainPageLocationMode} from '@/app/interfaces/preferences';
import {LocationPoint} from '@/common/interfaces/location';
import {useBooleanState} from '@/common/helpers/hooks/useBooleanState';
import {useRadioState} from '@/common/helpers/hooks/useRadioState';
import {useErrorInformerPush} from '@/common/services/ErrorInformer';
import {Interpolation, useLocalizer} from '@/common/services/localizer';
import {useRouter} from '@/common/services/router';
import {useUrlBuilder} from '@/app/routing/useUrlBuilder';
import {StoreState, StoreThunkDispatch} from '@/app/store';
import {updatePreferencesAction} from '@/app/store/config/actions';
import {selectDefaultLocationInfo, selectDomainConfig, selectMainPageLocationMode} from '@/app/store/config/selectors';
import {selectForecastLocation} from '@/app/store/forecast/selectors';
import {
	determinateCoordinates,
	determinateExactSettlement,
	determinateGeneralSettlement,
} from '@/app/store/geolocation/actions';
import {
	selectDeviceGeneralSettlement,
	selectDeviceGeolocationOrGeneralSettlement,
} from '@/app/store/geolocation/selectors';
import {setDefaultLocationConfigShown} from '@/app/store/ui/actions';
import {ServicesContext} from '@/app/contexts/ServicesContext';
import {ContainedButton} from '@/common/components/ContainedButton';
import {Dialog} from '@/common/components/Dialog';
import {InfoIcon} from '@/common/components/InfoIcon';
import {LocationSearch} from '@/common/components/LocationSearch';
import {TextButton} from '@/common/components/TextButton';
import {useGeoPermissionState} from '@/app/hooks/useGeoPermissionState';
import styles from './DefaultLocationConfig.css';

const searchSuggestCountLimit = 20;
const allowedLocationTypes = [LocationType.Settlement];

export const DefaultLocationConfig: FC = () => {
	const mainPageLocationMode = useSelector(selectMainPageLocationMode);

	const {backendAPI} = useContext(ServicesContext)!;
	const {history} = useRouter();
	const urlBuilder = useUrlBuilder();
	const {domainName} = useSelector(selectDomainConfig);
	const t = useLocalizer();
	const {language} = t;

	const dispatch = useDispatch<StoreThunkDispatch>();
	const store = useStore<StoreState>();
	const errorInformerPush = useErrorInformerPush();

	const [selectedMode, handleModeChange, setMode] = useRadioState<MainPageLocationMode>(
		mainPageLocationMode || 'determine',
	);

	const initialSelectedDefaultLocation = useSelector(selectDeviceGeolocationOrGeneralSettlement);
	const [selectedDefaultLocation, setSelectedDefaultLocation] = useState(() => {
		const state = store.getState();
		return selectDefaultLocationInfo(state) || initialSelectedDefaultLocation;
	});

	const [defaultLocationEditing, setDefaultLocationEditing, unsetDefaultLocationEditing] = useBooleanState(false);
	const [submitting, setSubmitting] = useState(false);

	const [geoPermissionState, updateGeoPermissionState] = useGeoPermissionState();

	const deviceType = useMemo(() => {
		const ua = navigator.userAgent;
		return /android/i.test(ua) || /iPad|iPhone|iPod/i.test(ua) ? 'mobile' : 'desktop';
	}, []);

	const handleInitEditDefaultLocation = async () => {
		setMode('defined');
		setDefaultLocationEditing();
	};

	const handleSelectDefaultLocation = useCallback(
		(location: LocationPoint) => {
			setSelectedDefaultLocation(location);
			unsetDefaultLocationEditing();
		},
		[selectedMode],
	);

	const handleClose = async () => {
		dispatch(setDefaultLocationConfigShown(false));
	};

	const handleSearch = useCallback(
		(searchString: string) => {
			return backendAPI.search.getSearchSuggests(
				searchString,
				t.language,
				searchSuggestCountLimit,
				allowedLocationTypes,
			);
		},
		[t.language],
	);

	const handleSubmit = async () => {
		setSubmitting(true);
		if (!selectedDefaultLocation) {
			return;
		}

		try {
			await dispatch(
				updatePreferencesAction(
					selectedMode === 'defined'
						? {
								mainPageLocationMode: selectedMode,
								defaultLocationId: selectedDefaultLocation.id,
								defaultLocationInfo: selectedDefaultLocation,
							}
						: {
								mainPageLocationMode: selectedMode,
								defaultLocationId: undefined,
								defaultLocationInfo: undefined,
							},
				),
			);
		} catch (error) {
			setSubmitting(false);
			errorInformerPush(error);
			throw error;
		}

		// Navigate to a new default location when it changed
		const currentLocation = selectForecastLocation(store.getState());
		if (selectedMode === 'defined' && currentLocation && currentLocation.id !== selectedDefaultLocation.id) {
			history!.push(urlBuilder(RouteName.weather, {location: selectedDefaultLocation}));
		}

		// Determinate geolocation and navigate to this location when the relevant mode is activated
		if (
			selectedMode === 'determine' &&
			(selectedMode !== mainPageLocationMode || geoPermissionState === 'prompt') &&
			geoPermissionState !== 'denied'
		) {
			let determinateResult;
			try {
				await dispatch(determinateCoordinates({timeout: 15000}));
				determinateResult = await dispatch(determinateExactSettlement({language}));
			} catch (error) {
				updateGeoPermissionState();
				handleClose();
				throw error;
			} finally {
				setSubmitting(false);
			}

			if (determinateResult.payload) {
				const location = determinateResult.payload.location;
				history!.push(urlBuilder(RouteName.weather, {location}));
			}
		}

		// Navigate to a general (approximate) determined location when no geolocation is allowed
		else if (selectedMode === 'determine' && selectedMode !== mainPageLocationMode) {
			if (!selectDeviceGeneralSettlement(store.getState())) {
				try {
					await dispatch(determinateGeneralSettlement({language}));
				} catch (error) {
					handleClose();
					throw error;
				} finally {
					setSubmitting(false);
				}
			}

			const location = selectDeviceGeneralSettlement(store.getState());
			if (location) {
				history!.push(urlBuilder(RouteName.weather, {location}));
			}
		}

		setSubmitting(false);

		handleClose();
	};

	// Reset editing on option switch from the "defined"
	useEffect(() => {
		if (defaultLocationEditing && selectedMode === 'determine') {
			unsetDefaultLocationEditing();
		}
	}, [selectedMode]);

	return (
		<Dialog title={t('mainPageConfigTitle')} onClose={handleClose}>
			<div className={styles.option}>
				<label className={styles.optionEntry}>
					<input
						className={styles.radioButton}
						type='radio'
						name='main-page-location-type'
						value='defined'
						checked={selectedMode === 'defined'}
						onChange={handleModeChange}
					/>
					<p className={styles.optionLabel}>{t('mainPageType.defined')}</p>
				</label>

				{!defaultLocationEditing ? (
					<div className={styles.optionLocationSection}>
						<div className={styles.optionLocationRow}>
							<p className={styles.locationCurrentName}>{selectedDefaultLocation?.title}</p>
							<TextButton
								className={styles.locationTailButton}
								sizeType='compact'
								onClick={handleInitEditDefaultLocation}>
								{t('change')}
							</TextButton>
						</div>
						{selectedDefaultLocation?.id === initialSelectedDefaultLocation?.id && (
							<div className={styles.alertText}>{t('defaultLocationDeterminedHint')}</div>
						)}
					</div>
				) : (
					<div className={styles.locationSearchRow}>
						<LocationSearch
							className={styles.locationSearch}
							fetchSuggest={handleSearch}
							placeholder={selectedDefaultLocation?.title}
							onSelect={handleSelectDefaultLocation}
						/>
						<TextButton
							className={styles.locationTailButton}
							sizeType='compact'
							onClick={unsetDefaultLocationEditing}>
							{t('cancel')}
						</TextButton>
					</div>
				)}
			</div>

			<div className={styles.option}>
				<label className={styles.optionEntry}>
					<input
						className={styles.radioButton}
						type='radio'
						name='main-page-location-type'
						value='determine'
						checked={selectedMode === 'determine'}
						onChange={handleModeChange}
					/>
					<p className={styles.optionLabel}>{t('mainPageType.determine')}</p>
				</label>

				{geoPermissionState === 'prompt' && (
					<div className={styles.alertText}>{t('geolocationWillRequired', {domainName})}</div>
				)}
				{geoPermissionState === 'denied' && (
					<div className={styles.alertText}>
						{t('geoAccessDisabledTitle')}
						<InfoIcon
							className={styles.alertInfo}
							title={
								<Interpolation
									l10nCode={`geoAccessDisabledInfo.${deviceType}`}
									domainName={() => <b>{domainName}</b>}
								/>
							}
							positionX='right'
							zIndex={31}
						/>
					</div>
				)}
			</div>

			<ContainedButton
				className={styles.submitButton}
				disabled={submitting || defaultLocationEditing}
				loading={submitting}
				onClick={handleSubmit}>
				{t('save')}
			</ContainedButton>
		</Dialog>
	);
};

DefaultLocationConfig.displayName = 'DefaultLocationConfig';
